import type { Vector3 } from "@pax/basemap";

/**
 * The flat map: the plate carrée, longitude and latitude as x and y, repeating east and west without
 * end. Directions are the world's, x toward 0° 0° and z north.
 */

/** `x` and `y` are the screen center's longitude and latitude in radians; `scale` is pixels a radian. */
export interface FlatCamera {
  kind: "flat";
  x: number;
  y: number;
  scale: number;
}

interface Size {
  width: number;
  height: number;
}

const HALF_PI = Math.PI / 2;

/** The scale with the whole world in view, once across. */
export const flatFit = (size: Size) => Math.min(size.width / (2 * Math.PI), size.height / Math.PI);

/** The world direction under a screen point, or null past a pole. */
export function flatAt(camera: FlatCamera, size: Size, px: number, py: number): Vector3 | null {
  const lon = camera.x + (px - size.width / 2) / camera.scale;
  const lat = camera.y + (size.height / 2 - py) / camera.scale;
  if (Math.abs(lat) > HALF_PI) return null;
  return [Math.cos(lat) * Math.cos(lon), Math.cos(lat) * Math.sin(lon), Math.sin(lat)];
}

/**
 * The camera at `scale`, its longitude wrapped and its latitude kept so the poles never leave the
 * screen's edges, or centered when the whole height fits.
 */
export function flatLookingAt(camera: FlatCamera, scale: number, size: Size): FlatCamera {
  const reach = Math.max(0, HALF_PI - size.height / 2 / scale);
  return {
    kind: "flat",
    scale,
    x: ((((camera.x + Math.PI) % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI)) - Math.PI,
    y: Math.min(reach, Math.max(-reach, camera.y)),
  };
}

/** The camera moved by a drag of the screen, the world following the pointer. */
export function flatPanned(camera: FlatCamera, size: Size, dx: number, dy: number): FlatCamera {
  return flatLookingAt(
    { ...camera, x: camera.x - dx / camera.scale, y: camera.y + dy / camera.scale },
    camera.scale,
    size,
  );
}

/** The camera zoomed by a factor about a screen point, which stays put. */
export function flatZoomed(
  camera: FlatCamera,
  size: Size,
  factor: number,
  px: number,
  py: number,
  limits: { least: number; most: number },
): FlatCamera {
  const scale = Math.min(limits.most, Math.max(limits.least, camera.scale * factor));
  const sx = (px - size.width / 2) / camera.scale;
  const sy = (size.height / 2 - py) / camera.scale;
  const ratio = camera.scale / scale;
  return flatLookingAt(
    { ...camera, x: camera.x + sx - sx * ratio, y: camera.y + sy - sy * ratio },
    scale,
    size,
  );
}
